import { Capacitor } from '@capacitor/core';
import type { AuthError, Session } from '@supabase/supabase-js';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { supabase } from '../supabase/client';

const DEFAULT_SESSION_WAIT_MS = 8000;
const SESSION_POLL_MS = 120;

/** signInWithIdToken 後輪詢直到 session 寫入 storage（Capacitor Preferences 可能略延遲） */
export async function waitForSupabaseAuthSession(options?: {
  timeoutMs?: number;
  intervalMs?: number;
}): Promise<{ session: Session | null; error: AuthError | null }> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_SESSION_WAIT_MS;
  const intervalMs = options?.intervalMs ?? SESSION_POLL_MS;
  const deadline = Date.now() + timeoutMs;
  let lastError: AuthError | null = null;

  while (Date.now() < deadline) {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      lastError = error;
    } else if (session) {
      return { session, error: null };
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return { session: null, error: lastError };
}

let initialized = false;

/** Google Cloud「Web application」OAuth Client ID（非 Android client ID） */
export function getGoogleWebClientId(): string | undefined {
  const id = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined)?.trim();
  return id || undefined;
}

export async function ensureNativeGoogleInitialized(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const webClientId = getGoogleWebClientId();
  if (!webClientId) {
    throw new Error(
      'App 尚未設定 Google Web Client ID（VITE_GOOGLE_WEB_CLIENT_ID）。請參考 docs/ANDROID_GOOGLE_SIGNIN.md'
    );
  }

  if (initialized) return;

  await SocialLogin.initialize({
    google: { webClientId },
  });
  initialized = true;
}

/**
 * Android / iOS 原生 Google 登入 → Supabase session（不開 Chrome OAuth 重定向）
 */
export async function signInWithNativeGoogle(): Promise<void> {
  await ensureNativeGoogleInitialized();

  let login;
  try {
    login = await SocialLogin.login({
      provider: 'google',
      options: {},
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (/cancel|cancelled|user/i.test(message)) {
      throw new Error('已取消 Google 登入');
    }
    throw err instanceof Error ? err : new Error(message);
  }

  if (login.provider !== 'google') {
    throw new Error('Google 登入回應異常');
  }

  const result = login.result;
  if (result.responseType === 'offline') {
    throw new Error('目前不支援 offline Google 登入模式');
  }

  const idToken = result.idToken;
  if (!idToken) {
    throw new Error('未取得 Google ID Token');
  }

  const { error } = await supabase.auth.signInWithIdToken({
    provider: 'google',
    token: idToken,
  });

  if (error) {
    throw new Error(error.message);
  }

  const { session, error: sessionError } = await waitForSupabaseAuthSession();
  if (sessionError) {
    throw new Error(sessionError.message);
  }
  if (!session) {
    throw new Error('Supabase 登入成功但未建立 session，請稍後再試');
  }
}

export function isNativeGoogleSignInAvailable(): boolean {
  return Capacitor.isNativePlatform() && Boolean(getGoogleWebClientId());
}
